/**
 * Editor do carrossel da Nex Lab.
 *
 * O sistema da Nex Lab é um carrossel, não uma arte solta: capa com card de
 * vidro, internas com fotos empilhadas, e um final fixo. Por isso este editor
 * monta a SEQUÊNCIA inteira e entrega as artes na ordem, já como carrossel, à
 * Nova peça — remontar à mão é onde a capa vira o slide 3.
 *
 * Como no editor do AntiFila (ver `artes.ts`), o desenho é fixo e o conteúdo é
 * variável: quem opera escolhe fotos, escreve e ajusta o enquadramento. Fonte,
 * cor, card e posição não são editáveis — é isso que o editor compra.
 *
 * As fotos vêm de `/media/:id/arquivo`, nunca da `publicUrl`: imagem de outra
 * origem contamina o canvas e só a exportação quebra (docs/EDITOR-DE-ARTES.md
 * §3.2).
 */
import {
  ALTURA,
  LARGURA,
  MAX_INTERNAS,
  TIPO,
  carrosselVazio,
  comFaixas,
  fotoVazia,
  pendencias,
  sequencia,
  type Carrossel,
  type Faixas,
  type FotoNaArte,
  type Slide
} from "../../arte/nexlab.js";
import { Api, type Marca, type MidiaGuardada } from "../api.js";
import {
  carregarRecursos,
  desenharSlide,
  type Medidas,
  type Recursos
} from "../arte/nexlab.js";
import { el, limpar } from "../dom.js";
import { entregarPeca } from "../passagem.js";
import { avisar } from "../tela.js";

const RASCUNHO = "nex-social:carrossel";

/**
 * O rascunho fica no navegador de quem edita, por marca.
 *
 * Conveniência, não dado do sistema — o que o sistema conhece são as artes
 * geradas, na biblioteca. Em try/catch porque em aba anônima o acesso lança.
 */
function lerRascunho(brand: string): Carrossel {
  try {
    const cru = localStorage.getItem(`${RASCUNHO}:${brand}`);
    if (!cru) return carrosselVazio();
    const lido = JSON.parse(cru) as Partial<Carrossel>;
    const vazio = carrosselVazio();
    // Mesclado sobre o padrão: campo novo no futuro não quebra rascunho velho.
    return {
      capa: { ...vazio.capa, ...(lido.capa ?? {}) },
      internas: Array.isArray(lido.internas) ? lido.internas : vazio.internas
    };
  } catch {
    return carrosselVazio();
  }
}

function salvarRascunho(brand: string, c: Carrossel): void {
  try {
    localStorage.setItem(`${RASCUNHO}:${brand}`, JSON.stringify(c));
  } catch {
    // Sem rascunho a tela funciona igual.
  }
}

function campo(
  rotulo: string,
  controle: HTMLElement,
  dica?: string
): HTMLElement {
  return el(
    "div",
    { class: "campo" },
    el("label", {}, rotulo),
    controle,
    dica ? el("p", { class: "sub" }, dica) : null
  );
}

function nomeDoSlide(s: Slide): string {
  if (s.tipo === "capa") return "Capa";
  if (s.tipo === "final") return "Final";
  return `Interna ${s.indice + 1}`;
}

/** Um canvas do tamanho real da arte. */
function canvasDaArte(): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = LARGURA;
  c.height = ALTURA;
  return c;
}

export async function editorCarrosselNexlab(
  raiz: HTMLElement,
  marca: Marca
): Promise<void> {
  const c = lerRascunho(marca.slug);
  /** Qual slide está no preview, e qual espaço de foto recebe o próximo clique. */
  let ativo = { slide: 0, foto: 0 };
  let recursos: Recursos | null = null;
  let biblioteca: MidiaGuardada[] = [];
  const imagens = new Map<number, HTMLImageElement>();

  // ------------------------------------------------------------- preview
  const preview = el("canvas", { class: "arte-preview" }) as HTMLCanvasElement;
  preview.width = LARGURA;
  preview.height = ALTURA;
  const ctx = preview.getContext("2d");
  if (!ctx) {
    raiz.appendChild(
      el("p", { class: "aviso erro" }, "Este navegador não tem canvas 2D.")
    );
    return;
  }

  const tira = el("div", { class: "tira-slides" });
  const legenda = el("p", { class: "sub" });
  const alertas = el("div", {});

  const painelCapa = el("div", {});
  const painelInternas = el("div", {});
  const painelFotos = el("div", {});

  /** Os slots da sequência que podem receber foto: a capa e as internas. */
  const fotosDoSlide = (i: number): FotoNaArte[] | null =>
    i === 0 ? c.capa.fotos : (c.internas[i - 1]?.fotos ?? null);

  let salvar: ReturnType<typeof setTimeout> | undefined;
  let miniaturas: ReturnType<typeof setTimeout> | undefined;

  const redesenhar = (): void => {
    const seq = sequencia(c);
    if (ativo.slide >= seq.length) ativo = { slide: seq.length - 1, foto: 0 };
    const slide = seq[ativo.slide]!;

    let medidas: Medidas | null = null;
    if (recursos) medidas = desenharSlide(ctx, slide, imagens, recursos);

    legenda.textContent =
      `${nomeDoSlide(slide)} — slide ${ativo.slide + 1} de ${seq.length}. ` +
      (slide.tipo === "final"
        ? "O final é fixo: usa as fotos do slide anterior por trás do vidro."
        : "Clique num espaço de foto e depois numa foto da biblioteca.");

    limpar(alertas);
    if (medidas && medidas.tituloTamanho < TIPO.titulo) {
      alertas.appendChild(
        el(
          "p",
          { class: "aviso" },
          `O título não cabe numa linha e encolheu para ${medidas.tituloTamanho}px ` +
            `(o sistema pede ${TIPO.titulo}). Um título mais curto fica mais forte.`
        )
      );
    }
    if (medidas && medidas.subtituloTamanho < TIPO.subtitulo) {
      alertas.appendChild(
        el(
          "p",
          { class: "aviso" },
          "O subtítulo é longo e encolheu para caber."
        )
      );
    }

    clearTimeout(miniaturas);
    miniaturas = setTimeout(pintarTira, 250);
    clearTimeout(salvar);
    salvar = setTimeout(() => salvarRascunho(marca.slug, c), 400);
  };

  /** Mudou a estrutura: quais campos e espaços existem. */
  const remontar = (): void => {
    montarCapa();
    montarInternas();
    montarFotos();
    redesenhar();
  };

  // ------------------------------------------------- tira de miniaturas
  //
  // Cada miniatura é desenhada no TAMANHO REAL num canvas de rascunho e só
  // então reduzida — mesma função do preview e da exportação, para o
  // título encolher no mesmo tamanho nos três.
  const rascunho = canvasDaArte();
  const rctx = rascunho.getContext("2d");

  function pintarTira(): void {
    limpar(tira);
    const seq = sequencia(c);
    seq.forEach((s, i) => {
      const mini = el("canvas", {
        class: i === ativo.slide ? "mini-slide ativo" : "mini-slide",
        title: nomeDoSlide(s)
      }) as HTMLCanvasElement;
      mini.width = 216;
      mini.height = 270;
      const m = mini.getContext("2d");
      if (m && rctx && recursos) {
        desenharSlide(rctx, s, imagens, recursos);
        m.drawImage(rascunho, 0, 0, mini.width, mini.height);
      }
      const botao = el(
        "button",
        { type: "button", class: "mini-botao" },
        mini,
        el("span", { class: "sub" }, `${i + 1} · ${nomeDoSlide(s)}`)
      );
      botao.addEventListener("click", () => {
        ativo = { slide: i, foto: 0 };
        remontar();
      });
      tira.appendChild(botao);
    });
  }

  // -------------------------------------------------------------- fotos
  const garantirFoto = (id: number): void => {
    if (imagens.has(id)) return;
    const img = new Image();
    // Mesma origem. Ver o cabeçalho do arquivo.
    img.src = `/media/${id}/arquivo`;
    img.addEventListener("load", () => redesenhar());
    img.addEventListener("error", () =>
      avisar(
        raiz,
        new Error(`Não foi possível carregar a mídia ${id}.`),
        "Foto"
      )
    );
    imagens.set(id, img);
  };

  /** A foto escolhida vai para o espaço ativo, e o foco pula para o próximo vazio. */
  const usarFoto = (id: number): void => {
    const fotos = fotosDoSlide(ativo.slide);
    if (!fotos) return;
    const alvo = fotos[ativo.foto];
    if (!alvo) return;
    alvo.mediaId = id;
    alvo.foco = 50;
    garantirFoto(id);

    const proximo = fotos.findIndex((f) => f.mediaId === null);
    if (proximo >= 0) ativo.foto = proximo;
    remontar();
  };

  /** Os espaços de foto de um slide, como botões. */
  function espacos(slide: number, fotos: FotoNaArte[]): HTMLElement {
    const linha = el("div", { class: "espacos" });
    fotos.forEach((f, i) => {
      const marcado = ativo.slide === slide && ativo.foto === i;
      const b = el("button", {
        type: "button",
        class: marcado ? "espaco ativo" : "espaco",
        title: `Foto ${i + 1}`
      });
      if (f.mediaId !== null) {
        const img = el("img", { alt: `foto ${i + 1}` }) as HTMLImageElement;
        img.src = `/media/${f.mediaId}/arquivo`;
        b.appendChild(img);
      } else {
        b.appendChild(el("span", { class: "sem-foto" }, `foto ${i + 1}`));
      }
      b.addEventListener("click", () => {
        ativo = { slide, foto: i };
        remontar();
      });
      linha.appendChild(b);
    });
    return linha;
  }

  /** 1, 2 ou 3 fotos empilhadas. */
  function seletorDeFaixas(
    atual: number,
    opcoes: Faixas[],
    aoEscolher: (n: Faixas) => void
  ): HTMLElement {
    const linha = el("div", { class: "acoes-linha" });
    for (const n of opcoes) {
      const b = el(
        "button",
        {
          type: "button",
          class:
            n === atual ? "secundario pequeno marcado" : "secundario pequeno"
        },
        n === 1 ? "1 foto" : `${n} fotos`
      );
      b.addEventListener("click", () => aoEscolher(n));
      linha.appendChild(b);
    }
    return linha;
  }

  // --------------------------------------------------------------- capa
  function texto(
    valor: string,
    aoMudar: (v: string) => void
  ): HTMLInputElement {
    const i = el("input", { type: "text" }) as HTMLInputElement;
    i.value = valor;
    i.addEventListener("input", () => {
      aoMudar(i.value);
      // Editar texto da capa traz a capa para o preview.
      if (ativo.slide !== 0) ativo = { slide: 0, foto: 0 };
      redesenhar();
    });
    return i;
  }

  // Os campos de texto são criados UMA vez: recriá-los a cada mudança de
  // estrutura tiraria o foco de quem está digitando.
  const camposDaCapa = el(
    "div",
    {},
    campo(
      "Título",
      texto(c.capa.titulo, (v) => (c.capa.titulo = v)),
      "Numa linha só. Se não couber, encolhe — e a tela avisa."
    ),
    campo(
      "Subtítulo",
      texto(c.capa.subtitulo, (v) => (c.capa.subtitulo = v)),
      "Ex.: “4ª edição”. A linha roxa segue até a borda."
    ),
    campo(
      "Local",
      texto(c.capa.local, (v) => (c.capa.local = v)),
      "A linha pequena depois do //. Ex.: “Loja Claro | Shopping Aricanduva”."
    ),
    campo(
      "Parceiro",
      texto(c.capa.parceiro, (v) => (c.capa.parceiro = v)),
      "Na pílula da direita. Ex.: “Nex Lab x AlmapBBDO”. Vazio, a pílula some."
    )
  );

  function montarCapa(): void {
    limpar(painelCapa);
    painelCapa.appendChild(
      campo(
        "Fotos da capa",
        el(
          "div",
          {},
          seletorDeFaixas(c.capa.fotos.length, [1, 2, 3], (n) => {
            c.capa.fotos = comFaixas(c.capa.fotos, n);
            ativo = { slide: 0, foto: 0 };
            remontar();
          }),
          espacos(0, c.capa.fotos)
        )
      )
    );
    painelCapa.appendChild(camposDaCapa);
  }

  // ----------------------------------------------------------- internas
  function montarInternas(): void {
    limpar(painelInternas);

    c.internas.forEach((interna, i) => {
      const slide = i + 1;
      const mover = (delta: number): void => {
        const j = i + delta;
        if (j < 0 || j >= c.internas.length) return;
        const [x] = c.internas.splice(i, 1);
        c.internas.splice(j, 0, x!);
        ativo = { slide: j + 1, foto: 0 };
        remontar();
      };

      const subir = el(
        "button",
        {
          type: "button",
          class: "secundario pequeno",
          title: "Mover para cima"
        },
        "↑"
      );
      subir.addEventListener("click", () => mover(-1));
      const descer = el(
        "button",
        {
          type: "button",
          class: "secundario pequeno",
          title: "Mover para baixo"
        },
        "↓"
      );
      descer.addEventListener("click", () => mover(1));
      const tirar = el(
        "button",
        {
          type: "button",
          class: "secundario pequeno",
          title: "Remover esta interna"
        },
        "remover"
      );
      tirar.addEventListener("click", () => {
        c.internas.splice(i, 1);
        ativo = {
          slide: Math.max(0, Math.min(ativo.slide, c.internas.length)),
          foto: 0
        };
        remontar();
      });

      painelInternas.appendChild(
        el(
          "div",
          { class: ativo.slide === slide ? "interna ativa" : "interna" },
          el(
            "div",
            { class: "acoes-linha" },
            el("strong", {}, `Interna ${i + 1}`),
            seletorDeFaixas(interna.fotos.length, [1, 2, 3], (n) => {
              interna.fotos = comFaixas(interna.fotos, n);
              ativo = { slide, foto: 0 };
              remontar();
            }),
            subir,
            descer,
            tirar
          ),
          espacos(slide, interna.fotos)
        )
      );
    });

    const mais = el(
      "button",
      { type: "button", class: "secundario" },
      "+ interna"
    ) as HTMLButtonElement;
    mais.disabled = c.internas.length >= MAX_INTERNAS;
    mais.addEventListener("click", () => {
      c.internas.push({ fotos: [fotoVazia(), fotoVazia()] });
      ativo = { slide: c.internas.length, foto: 0 };
      remontar();
    });
    painelInternas.appendChild(
      el(
        "div",
        { class: "acoes-linha" },
        mais,
        el(
          "span",
          { class: "sub" },
          `Até ${MAX_INTERNAS} — o Instagram aceita 10 itens, contando capa e final.`
        )
      )
    );
  }

  // --------------------------------------------------- biblioteca e foco
  function montarFotos(): void {
    limpar(painelFotos);

    const fotos = fotosDoSlide(ativo.slide);
    const alvo = fotos?.[ativo.foto];

    if (alvo && alvo.mediaId !== null) {
      const faixa = el("input", {
        type: "range",
        min: "0",
        max: "100",
        step: "1"
      }) as HTMLInputElement;
      faixa.value = String(alvo.foco);
      faixa.addEventListener("input", () => {
        alvo.foco = Number(faixa.value);
        redesenhar();
      });
      painelFotos.appendChild(
        campo(
          "Enquadramento da foto escolhida",
          faixa,
          "Onde o corte cai: à esquerda mostra o topo da foto, à direita a base."
        )
      );
    }

    const grade = el("div", { class: "escolher-midia escolher-foto" });
    for (const m of biblioteca) {
      const b = el("button", {
        type: "button",
        class:
          alvo?.mediaId === m.mediaId ? "opcao-midia marcada" : "opcao-midia",
        title: `mídia ${m.mediaId}`
      });
      const img = el("img", {
        alt: `mídia ${m.mediaId}`,
        loading: "lazy"
      }) as HTMLImageElement;
      img.src = `/media/${m.mediaId}/arquivo`;
      b.appendChild(img);
      b.addEventListener("click", () => usarFoto(m.mediaId));
      grade.appendChild(b);
    }
    if (biblioteca.length === 0) {
      grade.appendChild(
        el(
          "p",
          { class: "sub" },
          `Nenhuma foto de ${marca.nome} ainda. Envie acima.`
        )
      );
    }
    painelFotos.appendChild(
      fotos
        ? grade
        : el(
            "p",
            { class: "sub" },
            "O final não tem foto própria — escolha a capa ou uma interna."
          )
    );
  }

  // -------------------------------------------------------------- upload
  const entradaArquivo = el("input", {
    type: "file",
    accept: "image/*",
    multiple: true
  }) as HTMLInputElement;
  const botaoSubir = el(
    "button",
    { class: "secundario" },
    "Enviar fotos"
  ) as HTMLButtonElement;
  const saidaUpload = el("span", { class: "sub" });

  botaoSubir.addEventListener("click", () => {
    const arquivos = [...(entradaArquivo.files ?? [])];
    if (arquivos.length === 0) {
      saidaUpload.textContent = "Escolha ao menos um arquivo.";
      return;
    }
    botaoSubir.disabled = true;
    void (async () => {
      let enviados = 0;
      let falhas = 0;
      // Em série: o upload é bufferizado em memória dos dois lados, e vários
      // juntos derrubam a API pelo teto do PM2.
      for (const a of arquivos) {
        saidaUpload.textContent = `Enviando ${a.name}…`;
        try {
          await Api.enviarMidia(marca.slug, a);
          enviados++;
        } catch {
          falhas++;
        }
      }
      entradaArquivo.value = "";
      botaoSubir.disabled = false;
      saidaUpload.textContent = `${enviados} enviada(s)${falhas ? `, ${falhas} falhou(aram)` : ""}.`;
      biblioteca = (await Api.midias(marca.slug, "image")).media;
      montarFotos();
    })();
  });

  // --------------------------------------------------------------- gerar
  const botaoGerar = el("button", {}, "Gerar carrossel") as HTMLButtonElement;
  const saida = el("p", { class: "sub" });

  botaoGerar.addEventListener("click", () => {
    const faltas = pendencias(c);
    if (faltas.length > 0) {
      saida.className = "erro-linha";
      saida.textContent = faltas.map((f) => f.mensagem).join(" ");
      ativo = { slide: faltas[0]!.slide, foto: 0 };
      remontar();
      return;
    }
    if (!recursos) {
      saida.className = "erro-linha";
      saida.textContent = "Os elementos do sistema ainda não carregaram.";
      return;
    }

    const seq = sequencia(c);
    const r = recursos;
    botaoGerar.disabled = true;
    saida.className = "sub";

    void (async () => {
      try {
        const ids: number[] = [];
        // Canvas próprio da exportação, um slide por vez, em série: cada
        // upload é bufferizado em memória no servidor.
        const fora = canvasDaArte();
        const fctx = fora.getContext("2d");
        if (!fctx)
          throw new Error("Não foi possível criar o canvas de exportação.");

        for (const [i, s] of seq.entries()) {
          saida.textContent = `Gerando ${i + 1} de ${seq.length} — ${nomeDoSlide(s)}…`;
          desenharSlide(fctx, s, imagens, r);
          const blob = await new Promise<Blob>((ok, erro) =>
            fora.toBlob(
              (b) =>
                b
                  ? ok(b)
                  : erro(
                      new Error("O navegador não devolveu os bytes da arte.")
                    ),
              "image/png"
            )
          );
          const enviada = await Api.enviarMidia(marca.slug, blob);
          ids.push(enviada.mediaId);
        }

        // Para a Nova peça, já como carrossel e na ordem. Ver src/ui/passagem.ts.
        entregarPeca({ brand: marca.slug, format: "CAROUSEL", mediaIds: ids });
        location.hash = "#/nova";
      } catch (err) {
        saida.className = "erro-linha";
        saida.textContent = err instanceof Error ? err.message : String(err);
        botaoGerar.disabled = false;
      }
    })();
  });

  // ------------------------------------------------------------ montagem
  raiz.appendChild(
    el(
      "div",
      { class: "arte-editor" },
      el(
        "div",
        {},
        el("div", { class: "caixa" }, el("h3", {}, "Capa"), painelCapa),
        el(
          "div",
          { class: "caixa" },
          el("h3", {}, "Internas"),
          el(
            "p",
            { class: "sub" },
            "Fotos empilhadas, com a tag e a seta do sistema. O final entra sozinho depois da última."
          ),
          painelInternas
        ),
        el(
          "div",
          { class: "caixa" },
          el("h3", {}, `Fotos de ${marca.nome}`),
          el(
            "div",
            { class: "acoes-linha" },
            entradaArquivo,
            botaoSubir,
            saidaUpload
          ),
          painelFotos
        )
      ),
      el(
        "div",
        { class: "arte-lado" },
        el(
          "div",
          { class: "caixa" },
          preview,
          legenda,
          alertas,
          tira,
          el("div", { class: "acoes" }, botaoGerar),
          saida,
          el(
            "p",
            { class: "sub" },
            "Gera as artes na ordem, manda para a biblioteca de " +
              `${marca.nome} e abre a Nova peça com o carrossel montado.`
          )
        )
      )
    )
  );

  remontar();

  // Fontes e SVGs ANTES do desenho de verdade: com a fonte de fallback o
  // título encolheria no tamanho errado, sem erro nenhum.
  try {
    recursos = await carregarRecursos();
  } catch (err) {
    avisar(
      raiz,
      err,
      "Não deu para carregar os elementos do sistema da Nex Lab"
    );
  }

  try {
    biblioteca = (await Api.midias(marca.slug, "image")).media;
  } catch (err) {
    avisar(raiz, err, "Não deu para listar a biblioteca");
  }

  // Repõe as fotos do rascunho.
  for (const f of [c.capa.fotos, ...c.internas.map((i) => i.fotos)].flat()) {
    if (f.mediaId !== null) garantirFoto(f.mediaId);
  }
  remontar();
}
